import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Card, Dialog, Skeleton } from "../../../design-v2";
import {
  getWorkoutLogHub,
  takeoverWorkoutExecution,
} from "../api/workoutLogApi";
import {
  createHydratedLocalExecutionRecord,
  EXECUTION_ACTIONS,
  executionReducer,
} from "../domain/executionReducer";
import {
  executionStatus,
  isTerminalExecution,
} from "../domain/executionReconciliation";
import { executionCounts } from "../domain/executionUi";
import { finalizeIfDue } from "../domain/finalization";
import { isWorkoutLogEnabled } from "../domain/featureFlag";
import {
  quarantineNoticeCopy,
  unacknowledgedQuarantineNotices,
} from "../domain/syncIssue";
import useConnectivity from "../hooks/useConnectivity";
import { finalizationAction } from "../hooks/useExecution";
import {
  EXECUTION_OPEN_ELSEWHERE,
  EXECUTION_OPEN_ELSEWHERE_MESSAGE,
  executionOpenElsewhereError,
  withExecutionLock,
} from "../hooks/useExecutionOwnership";
import RecoveryDialog from "../components/RecoveryDialog";
import SyncAuthNotice from "../components/SyncAuthNotice";
import useTrainIdentity, { canUseServer, needsSignInToSync } from "../recovery/trainIdentity";
import { selectLocalOpenExecutions } from "../recovery/localRecoveryProbe";
import { getOrCreateDeviceId } from "../store/deviceIdentity";
import { workoutLogStore } from "../store/localExecutionStore";
import { workoutSyncEngine } from "../sync/syncEngine";
import { buildOrigin } from "../../weeklyPlans/navigation";
import { getCycleDetailsPath } from "../../multiWeek/routes";

const HOUR = 60 * 60 * 1000;
const OPEN_STATUSES = new Set(["ACTIVE", "FINISHED"]);
const FLAG_ENABLED = isWorkoutLogEnabled();
const SHORT_MONTHS = Object.freeze([
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
]);

function formatDateKey(dateKey) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateKey || ""));
  if (!match) return "";
  const month = SHORT_MONTHS[Number(match[2]) - 1];
  return month ? `${month} ${Number(match[3])}` : "";
}

function displayTemporalStatus(status) {
  return status === "UPCOMING" ? "UP NEXT" : status;
}

function occurrenceSelectionKey(occurrence) {
  return occurrence
    ? `${occurrence.cycleId || occurrence.startTarget?.cycleId}:${occurrence.occurrenceKey}`
    : "";
}

function isStale(record) {
  const value = record?.timing?.lastActivityAt || record?.document?.finishedAt;
  return value ? Date.now() - new Date(value).getTime() >= HOUR : false;
}

function occurrenceStartPath(occurrence) {
  const params = new URLSearchParams({
    kind: "CYCLE_OCCURRENCE",
    cycleId: occurrence.startTarget.cycleId,
    occurrenceKey: occurrence.startTarget.occurrenceKey,
  });
  return `/train/start?${params}`;
}

// Local-first precedence: a SAVED/DISCARDED record on this device (pending or
// acknowledged) is the current truth for its executionId. The server can still
// report that execution ACTIVE/FINISHED until the terminal document arrives,
// and that older open copy is never offered for recovery again.
function selectRecoverableServerExecutions(openExecutions = [], records = []) {
  const locallyTerminal = new Set(
    records.filter(isTerminalExecution).map((record) => record.executionId)
  );
  return (openExecutions || []).filter((entry) => !locallyTerminal.has(entry.executionId));
}

function recoveryExecutionId(target) {
  return target?.record?.executionId || target?.summary?.executionId || null;
}

// The execution editor, for an ACTIVE and for a FINISHED workout alike. Review
// is read-only, so "Continue Editing" / "Keep Editing" never lead there: a
// FINISHED workout opens its editor the way Review's back arrow does (still
// FINISHED, timing frozen; the editor's own Finish returns to Review).
function executionEditorLocation(record) {
  return {
    path: `/train/session/${record.executionId}`,
    state: executionStatus(record) === "FINISHED" ? { editFinished: true } : undefined,
  };
}

function openLocation(navigate, { path, state }) {
  navigate(path, state ? { state } : undefined);
}

function serverOwnedElsewhere(record, openExecutions = []) {
  const server = openExecutions.find((entry) => entry.executionId === record?.executionId);
  return server?.ownerDeviceId && server.ownerDeviceId !== record?.deviceId
    ? server
    : null;
}

function HistoryIcon() {
  return (
    <svg data-icon="history-recent" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="7.75" />
      <path d="M4.25 4.75v4.5h4.5M4.45 9.15A8.25 8.25 0 0 1 12 3.75" />
      <path d="M12 7.5V12l3 2" />
    </svg>
  );
}

function ArrowIcon({ direction = "right" }) {
  return (
    <svg className={`lz-workout-arrow lz-workout-arrow--${direction}`} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="m9 5 7 7-7 7" />
    </svg>
  );
}

function ChooseIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M5 6h12M5 10h12M5 14h7" />
      <path d="m15 13 5 3.5-5 3.5v-7Z" />
    </svg>
  );
}

function CurrentWeekSessionRail({
  occurrences = [],
  nextMondayGraceOccurrence = null,
  selectedOccurrence = null,
  onSelect,
}) {
  if (!occurrences.length && !nextMondayGraceOccurrence) return null;

  const selectedKey = occurrenceSelectionKey(selectedOccurrence);
  return (
    <div className="lz-workout-hub-card__rail">
      {occurrences.length ? (
        <div
          className="lz-workout-session-rail"
          role="group"
          aria-label="Current week sessions"
        >
          {occurrences.map((occurrence, index) => {
            const key = occurrenceSelectionKey(occurrence);
            const hasDayNumber = occurrence.dayNumber != null &&
              Number.isFinite(Number(occurrence.dayNumber));
            const dayNumber = hasDayNumber
              ? Number(occurrence.dayNumber)
              : index + 1;
            const completed = Boolean(occurrence.completed);
            const selected = !completed && selectedKey === key;
            return (
              <button
                key={key}
                type="button"
                className={`lz-workout-session-pill${selected ? " is-selected" : ""}${completed ? " is-completed" : ""}`}
                aria-label={`Day ${dayNumber}${completed ? ", completed" : selected ? ", selected" : ""}`}
                aria-pressed={selected}
                disabled={completed}
                onClick={() => onSelect(occurrence)}
              >
                {completed ? <span aria-hidden="true">✓</span> : null}
                <span>D{dayNumber}</span>
              </button>
            );
          })}
        </div>
      ) : null}

      {nextMondayGraceOccurrence ? (
        <button
          type="button"
          className={`lz-workout-grace-selector${selectedKey === occurrenceSelectionKey(nextMondayGraceOccurrence) ? " is-selected" : ""}`}
          aria-label={`Next week, Monday, Day ${nextMondayGraceOccurrence.dayNumber || 1}`}
          aria-pressed={selectedKey === occurrenceSelectionKey(nextMondayGraceOccurrence)}
          onClick={() => onSelect(nextMondayGraceOccurrence)}
        >
          <span>NEXT WEEK</span>
          <strong>MON · D{nextMondayGraceOccurrence.dayNumber || 1}</strong>
        </button>
      ) : null}
    </div>
  );
}

function HubSkeleton() {
  return (
    <div className="lz-workout-hub-skeleton" aria-label="Loading workouts">
      <Skeleton shape="block" className="lz-workout-hub-skeleton__hero" />
      <Skeleton shape="text" width="9rem" />
      <Skeleton shape="block" className="lz-workout-hub-skeleton__row" />
      <Skeleton shape="block" className="lz-workout-hub-skeleton__row" />
    </div>
  );
}

function ProgramPlaceholderHero({ currentProgram, hubAvailable, online, onCreate }) {
  const noProgram = hubAvailable && currentProgram === null;
  const hasProgram = Boolean(currentProgram);
  const title = noProgram
    ? "NO ACTIVE PROGRAM"
    : hasProgram
      ? currentProgram.name
      : "Program unavailable";
  const description = noProgram
    ? "Create your first training program to get guided workouts here."
    : hasProgram
      ? "No workout is currently available to start from this program."
      : online
        ? "We couldn’t load your current program right now."
        : "Connect to load your current program.";
  const status = noProgram
    ? "GET STARTED"
    : hasProgram && currentProgram.temporalStatus === "upcoming"
      ? "UPCOMING"
      : hasProgram
        ? "NO WORKOUT"
        : online
          ? "UNAVAILABLE"
          : "OFFLINE";

  return (
    <article className="lz-workout-hub-card lz-workout-hub-card--locked" aria-labelledby="program-placeholder-title">
      <div className="lz-workout-hub-card__context">
        <span className="lz-workout-hub-card__pill lz-workout-hub-card__pill--muted">
          <span aria-hidden="true" /> <span>{noProgram ? "PROGRAM STATUS" : "CURRENT PROGRAM"}</span>
        </span>
        <span className="lz-workout-hub-card__status lz-workout-hub-card__status--muted">
          {status}
        </span>
      </div>
      <div className="lz-workout-hub-card__body lz-workout-hub-card__body--placeholder">
        <h2 id="program-placeholder-title">{title}</h2>
        <p className="lz-workout-hub-card__description">{description}</p>
      </div>
      {noProgram ? (
        <button type="button" className="lz-workout-hub-card__start" onClick={onCreate}>
          <span>Create a Program</span>
          <ArrowIcon />
        </button>
      ) : (
        <button type="button" className="lz-workout-hub-card__start" disabled>
          <span>{hasProgram ? "No Workout Available" : "Program Unavailable"}</span>
        </button>
      )}
    </article>
  );
}

function ProgramWeekStateHero({ currentProgram, currentWeek, sessionRail, onViewProgram }) {
  const complete = currentWeek.state === "WEEK_COMPLETE";
  const status = complete ? "WEEK COMPLETE" : "NO WORKOUTS";
  const position = complete
    ? `WEEK ${currentWeek.weekNumber}`
    : "NO WORKOUTS THIS WEEK";
  const description = complete
    ? "All workouts for this week are complete."
    : "Your current program has no scheduled workouts this week.";

  return (
    <article
      className="lz-workout-hub-card lz-workout-hub-card--locked lz-workout-hub-card--week-state"
      aria-labelledby="program-week-state-title"
    >
      <div className="lz-workout-hub-card__context">
        <span className="lz-workout-hub-card__pill lz-workout-hub-card__pill--muted">
          <span aria-hidden="true" /> <span>CURRENT PROGRAM</span>
        </span>
        <span className="lz-workout-hub-card__status lz-workout-hub-card__status--muted">
          {status}
        </span>
      </div>
      {sessionRail}
      <div className="lz-workout-hub-card__body lz-workout-hub-card__body--placeholder">
        <p className="lz-workout-hub-card__position">{position}</p>
        <h2 id="program-week-state-title">{currentProgram.name}</h2>
        <p className="lz-workout-hub-card__description">{description}</p>
      </div>
      <button
        type="button"
        className="lz-workout-hub-card__start lz-workout-hub-card__start--secondary"
        onClick={onViewProgram}
      >
        <span>View Program</span>
        <ArrowIcon />
      </button>
    </article>
  );
}

export default function TrainHub({ featureEnabled = FLAG_ENABLED } = {}) {
  // Local reads use the TRAIN execution identity's partition; every server
  // read, takeover and Start entry also needs an authenticated identity. A
  // local recovery identity reads IndexedDB only (§9.8).
  const identity = useTrainIdentity();
  const userId = identity?.userId || null;
  const serverMode = canUseServer(identity);
  const { online: browserOnline } = useConnectivity();
  // Server-backed sections treat local recovery like offline: "Needs a
  // connection", never fabricated content.
  const online = browserOnline && serverMode;
  const navigate = useNavigate();
  const location = useLocation();
  const [localRecords, setLocalRecords] = useState([]);
  const [hub, setHub] = useState(null);
  const [selectedOccurrenceKey, setSelectedOccurrenceKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recovery, setRecovery] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmDiscard, setConfirmDiscard] = useState(null);
  const [lockBlocked, setLockBlocked] = useState(false);
  const [quarantineNotices, setQuarantineNotices] = useState([]);
  const blockedFinalizationRef = useRef(new Set());
  // Automatic (effect-driven) lifecycle writes already running in this tab, so
  // a re-run effect (StrictMode, a reload) never races this tab's own lock.
  const automaticWritesRef = useRef(new Set());
  const localOpen = useMemo(() => selectLocalOpenExecutions(localRecords), [localRecords]);
  const serverOpen = useMemo(
    () => selectRecoverableServerExecutions(hub?.openExecutions, localRecords),
    [hub, localRecords]
  );

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    const records = await workoutLogStore.listExecutions(userId).catch(() => []);
    setLocalRecords(records);
    if (online) {
      try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const response = await getWorkoutLogHub(timezone);
        setSelectedOccurrenceKey(null);
        setHub(response);
      } catch (_error) {
        setHub(null);
      }
    }
    setLoading(false);
  }, [online, userId]);

  useEffect(() => { void load(); }, [load, location.state]);

  // Writes the server rejected and quarantined are explained here until the
  // athlete acknowledges them, including after a reload or cold start.
  const loadQuarantineNotices = useCallback(async () => {
    if (!userId) return;
    try {
      setQuarantineNotices(unacknowledgedQuarantineNotices(
        await workoutLogStore.listQuarantine(userId)
      ));
    } catch (_error) {
      setQuarantineNotices([]);
    }
  }, [userId]);

  useEffect(() => {
    void loadQuarantineNotices();
    const unsubscribe = workoutSyncEngine.subscribe?.((event) => {
      if (event?.type === "EXECUTION_QUARANTINED") {
        void loadQuarantineNotices();
        void load();
      }
    });
    return () => unsubscribe?.();
  }, [load, loadQuarantineNotices, location.state]);

  async function acknowledgeQuarantineNotices() {
    const ids = quarantineNotices.map((entry) => entry.id);
    setQuarantineNotices([]);
    await workoutLogStore.acknowledgeQuarantineNotices(userId, ids).catch(() => null);
    void loadQuarantineNotices();
  }

  const quarantineCopy = quarantineNoticeCopy(quarantineNotices);

  // Every Hub lifecycle write (lazy finalization, Resume activity, Finish,
  // Save, Discard, takeover hydration) runs under the same per-execution editor
  // lock as ExecutionScreen and acts on the durable record read under that
  // lock, never on the Hub's earlier snapshot. When another tab owns the
  // execution nothing is dispatched, persisted or synced.
  const lockedLifecycleWrite = useCallback(async (executionId, work) => {
    const outcome = await withExecutionLock(executionId, async () => work(
      await workoutLogStore.getExecution(userId, executionId)
    ));
    if (!outcome.acquired) throw executionOpenElsewhereError();
    return outcome.value;
  }, [userId]);

  const persistLifecycleWrite = useCallback(async (next, syncReason) => {
    const persisted = (await workoutLogStore.putExecution(next)) || next;
    const accepted = Number(persisted.localRevision) >= Number(next.localRevision) &&
      executionStatus(persisted) === executionStatus(next);
    if (accepted) workoutSyncEngine.trigger(syncReason, { userId });
    return persisted;
  }, [userId]);

  const canonicalRecord = useCallback(async (target, durable) => {
    if (target.record) return durable;
    // Takeover is online-only and never runs for a local identity (§9.8).
    if (!online) throw new Error("Needs a connection");
    const deviceId = await getOrCreateDeviceId();
    const response = await takeoverWorkoutExecution(target.summary.executionId, deviceId);
    const hydrated = createHydratedLocalExecutionRecord({
      userId,
      deviceId,
      execution: response.execution,
      document: response.document,
      // Additive since Phase 9 (9C.1): absent on an older server or a
      // history failure. Local context only; never a revision or a sync.
      history: response.history,
      // Exercise notes carried from saved history (additive, like the history).
      exerciseNotes: response.exerciseNotes,
    });
    return (await workoutLogStore.putExecution(hydrated)) || hydrated;
  }, [online, userId]);

  useEffect(() => {
    if (loading || recovery) return;
    const record = localOpen[0];
    if (record) {
      const otherOwner = serverOwnedElsewhere(record, serverOpen);
      if (otherOwner) {
        setRecovery({
          summary: otherOwner,
          mode: String(otherOwner.status || record.document.status).toLowerCase(),
          otherDevice: true,
        });
        return;
      }
      const due = finalizeIfDue(record);
      if (due.due) {
        const { executionId } = record;
        if (
          blockedFinalizationRef.current.has(executionId) ||
          automaticWritesRef.current.has(executionId)
        ) {
          return;
        }
        automaticWritesRef.current.add(executionId);
        lockedLifecycleWrite(executionId, async (durable) => {
          const action = durable && OPEN_STATUSES.has(executionStatus(durable))
            ? finalizationAction(durable)
            : null;
          if (!action) return null;
          return persistLifecycleWrite(executionReducer(durable, action), "lazy-finalization");
        }).then(() => load(), (caught) => {
          if (caught?.code === EXECUTION_OPEN_ELSEWHERE) {
            // The owning tab finalizes it; retrying here would only loop.
            blockedFinalizationRef.current.add(executionId);
            setError(EXECUTION_OPEN_ELSEWHERE_MESSAGE);
            return;
          }
          setError(caught?.message || "This workout couldn't be finalized.");
        }).finally(() => automaticWritesRef.current.delete(executionId));
        return;
      }
      if (record.document.status === "ACTIVE" && !isStale(record)) {
        navigate(`/train/session/${record.executionId}`, { replace: true });
      } else {
        setRecovery({ record, mode: record.document.status.toLowerCase(), otherDevice: false });
      }
      return;
    }
    const server = serverOpen[0];
    if (server) {
      getOrCreateDeviceId().then((deviceId) => {
        const target = {
          summary: server,
          mode: server.status.toLowerCase(),
          otherDevice: server.ownerDeviceId !== deviceId,
        };
        const recentSameDevice = !target.otherDevice && server.status === "ACTIVE" &&
          Date.now() - new Date(server.lastActivityAt).getTime() < HOUR;
        if (!recentSameDevice) {
          setRecovery(target);
          return;
        }
        if (automaticWritesRef.current.has(server.executionId)) return;
        automaticWritesRef.current.add(server.executionId);
        setBusy(true);
        lockedLifecycleWrite(server.executionId, (durable) => canonicalRecord(target, durable))
          .then((hydrated) => {
            navigate(`/train/session/${hydrated.executionId}`, { replace: true });
          }).catch((caught) => {
            if (caught?.code === EXECUTION_OPEN_ELSEWHERE) setLockBlocked(true);
            else setError(caught?.message || "This workout couldn't be recovered.");
            setRecovery(target);
          }).finally(() => {
            automaticWritesRef.current.delete(server.executionId);
            setBusy(false);
          });
      });
    }
  }, [
    canonicalRecord,
    load,
    loading,
    localOpen,
    lockedLifecycleWrite,
    navigate,
    persistLifecycleWrite,
    recovery,
    serverOpen,
  ]);

  async function act(action) {
    const target = recovery;
    if (!target) return;
    if (action === "discard") {
      setConfirmDiscard(target);
      return;
    }
    setLockBlocked(false);
    if (action === "continue" && target.record) {
      // Continue Editing is navigation only: FINISHED stays FINISHED, timing
      // stays frozen and no revision is written. The editor screen applies the
      // normal ownership rule before it becomes editable.
      openLocation(navigate, executionEditorLocation(target.record));
      return;
    }
    setBusy(true);
    setError("");
    try {
      const outcome = await lockedLifecycleWrite(recoveryExecutionId(target), async (durable) => {
        let record = await canonicalRecord(target, durable);
        const status = executionStatus(record);
        if (!record || !OPEN_STATUSES.has(status)) return { stale: true };
        if (action === "resume" || action === "continue") {
          if (action === "resume" && status === "ACTIVE") {
            // "Resume Workout" from recovery is an ACTIVE activity event (§8.3).
            record = await persistLifecycleWrite(
              executionReducer(record, { type: EXECUTION_ACTIONS.NAVIGATE_EXERCISE }),
              "navigation"
            );
          }
          return executionEditorLocation(record);
        }
        if (action === "finish") {
          // Review needs real logged work: an empty ACTIVE workout opens its
          // editor instead, where Finish stays locked and Discard is offered.
          if (status === "ACTIVE" && executionCounts(record).performed === 0) {
            return executionEditorLocation(record);
          }
          if (status === "ACTIVE") {
            record = await persistLifecycleWrite(executionReducer(record, {
              type: EXECUTION_ACTIONS.FINISH,
              finishTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            }), "finish");
          }
          return { path: `/train/session/${record.executionId}/review` };
        }
        if (action === "save" && status === "FINISHED") {
          if (executionCounts(record).performed === 0) return { emptySave: record };
          record = await persistLifecycleWrite(
            executionReducer(record, { type: EXECUTION_ACTIONS.SAVE }),
            "save"
          );
          return { path: `/train/session/${record.executionId}/review` };
        }
        return { stale: true };
      });
      if (outcome.stale) {
        // The durable record moved on (for example another tab saved it):
        // nothing was written, so show the current state instead.
        setRecovery(null);
        await load();
      } else if (outcome.emptySave) {
        setConfirmDiscard({ record: outcome.emptySave, empty: true });
      } else {
        openLocation(navigate, outcome);
      }
    } catch (caught) {
      if (caught?.code === EXECUTION_OPEN_ELSEWHERE) setLockBlocked(true);
      else setError(caught?.message || "This workout couldn't be recovered.");
    } finally {
      setBusy(false);
    }
  }

  async function discardConfirmed() {
    const target = confirmDiscard;
    if (!target) return;
    setBusy(true);
    setError("");
    setLockBlocked(false);
    try {
      await lockedLifecycleWrite(recoveryExecutionId(target), async (durable) => {
        const record = await canonicalRecord(target, durable);
        if (!record || !OPEN_STATUSES.has(executionStatus(record))) return null;
        return persistLifecycleWrite(
          executionReducer(record, { type: EXECUTION_ACTIONS.DISCARD }),
          "discard"
        );
      });
      setConfirmDiscard(null);
      setRecovery(null);
      await load();
    } catch (caught) {
      if (caught?.code === EXECUTION_OPEN_ELSEWHERE) setLockBlocked(true);
      else setError(caught?.message || "This workout couldn't be discarded.");
    } finally {
      setBusy(false);
    }
  }

  const currentWeekOccurrences = useMemo(
    () => hub?.currentWeek?.occurrences || [],
    [hub]
  );
  const nextMondayGraceOccurrence = hub?.currentWeek?.nextMondayGraceOccurrence || null;
  const recommendedOccurrence = hub?.currentWeek?.state === "RECOMMENDED"
    ? hub.currentWeek.occurrence
    : null;
  const currentOccurrence = useMemo(() => {
    if (!selectedOccurrenceKey) return recommendedOccurrence;
    return [...currentWeekOccurrences, nextMondayGraceOccurrence]
      .filter(Boolean)
      .find((occurrence) => occurrenceSelectionKey(occurrence) === selectedOccurrenceKey) ||
      recommendedOccurrence;
  }, [currentWeekOccurrences, nextMondayGraceOccurrence, recommendedOccurrence, selectedOccurrenceKey]);
  const currentLabel = displayTemporalStatus(currentOccurrence?.temporalStatus || "");
  const currentOperationalDate = formatDateKey(currentOccurrence?.operationalDate);
  const sessionRail = (
    <CurrentWeekSessionRail
      occurrences={currentWeekOccurrences}
      nextMondayGraceOccurrence={nextMondayGraceOccurrence}
      selectedOccurrence={currentOccurrence}
      onSelect={(occurrence) => setSelectedOccurrenceKey(occurrenceSelectionKey(occurrence))}
    />
  );

  return (
    <section className="lz-workout-hub">
      <header className="lz-workout-hub__header">
        <div>
          <h1>TRAIN</h1>
          <p>Choose a workout and start training.</p>
        </div>
        <button
          type="button"
          className="lz-workout-hub__history"
          aria-label="Workout history unavailable"
          title="Workout history is not available yet"
          disabled
        >
          <HistoryIcon />
        </button>
      </header>
      {error ? <p className="lz-workout-error" role="alert">{error}</p> : null}
      {/* A signed-out device with this user's local Workout data (§11.4). */}
      <SyncAuthNotice userId={userId} visible={needsSignInToSync(identity)} />
      {quarantineCopy ? (
        <Card tone="muted" className="lz-workout-sync-notice">
          <div role="status">
            <h2>{quarantineCopy.title}</h2>
            <p>{quarantineCopy.detail}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={acknowledgeQuarantineNotices}>Got it</Button>
        </Card>
      ) : null}

      {featureEnabled && loading ? <HubSkeleton /> : featureEnabled ? (
        <>
          {currentOccurrence ? (
            <article className="lz-workout-hub-card" aria-labelledby="current-workout-title">
              <div className="lz-workout-hub-card__context">
                <span className="lz-workout-hub-card__pill">
                  <span aria-hidden="true" /> <span>CURRENT PROGRAM</span>
                </span>
                <span className="lz-workout-hub-card__status-block">
                  <span className="lz-workout-hub-card__status">{currentLabel}</span>
                  {currentOperationalDate ? (
                    <span className="lz-workout-hub-card__date">{currentOperationalDate}</span>
                  ) : null}
                </span>
              </div>
              {sessionRail}
              <div className="lz-workout-hub-card__body">
                <p className="lz-workout-hub-card__position">
                  WEEK {currentOccurrence.weekNumber} · DAY {currentOccurrence.dayNumber}
                </p>
                <h2 id="current-workout-title">{currentOccurrence.workoutName}</h2>
                <p className="lz-workout-hub-card__meta">
                  <span>{currentOccurrence.exerciseCount} exercises</span>
                  <span aria-hidden="true">·</span>
                  <span>~{currentOccurrence.estimatedDurationMinutes} min</span>
                </p>
              </div>
              <button
                type="button"
                className="lz-workout-hub-card__start"
                disabled={!online}
                onClick={() => navigate(occurrenceStartPath(currentOccurrence), { state: { occurrence: currentOccurrence } })}
              >
                <span>Start Workout</span>
                <ArrowIcon />
              </button>
            </article>
          ) : hub?.currentProgram && ["WEEK_COMPLETE", "NO_WORKOUTS_THIS_WEEK"].includes(
            hub?.currentWeek?.state
          ) ? (
            <ProgramWeekStateHero
              currentProgram={hub.currentProgram}
              currentWeek={hub.currentWeek}
              sessionRail={sessionRail}
              onViewProgram={() => navigate(getCycleDetailsPath(hub.currentProgram.cycleId))}
            />
          ) : (
            <ProgramPlaceholderHero
              currentProgram={hub?.currentProgram}
              hubAvailable={hub !== null}
              online={online}
              onCreate={() => navigate("/program/manual-new", {
                state: { from: buildOrigin(location), returnTo: "/program" },
              })}
            />
          )}

          <section className="lz-workout-hub-options" aria-labelledby="other-options-title">
            <h2 id="other-options-title">OTHER OPTIONS</h2>
            <div className="lz-workout-hub-options__list">
              <button
                type="button"
                className="lz-workout-option-row"
                disabled={!online}
                onClick={() => navigate("/train/start?kind=FREE")}
              >
                <span className="lz-workout-option-row__icon" aria-hidden="true">+</span>
                <span className="lz-workout-option-row__copy">
                  <strong>Free Workout</strong>
                  <span>Start from scratch and add exercises as you go.</span>
                </span>
                <ArrowIcon />
              </button>

              <button
                type="button"
                className="lz-workout-option-row"
                disabled={!online}
                onClick={() => navigate("/train/choose")}
              >
                <span className="lz-workout-option-row__icon" aria-hidden="true"><ChooseIcon /></span>
                <span className="lz-workout-option-row__copy">
                  <strong>Choose Workout</strong>
                  <span>Train another workout from your programs.</span>
                </span>
                <ArrowIcon />
              </button>
            </div>
            {!online ? <p className="lz-workout-offline-reason">Starting a workout needs a connection.</p> : null}
          </section>
        </>
      ) : !loading && !localOpen.length && !serverOpen.length ? (
        <Card><p>Workout Log is not currently available.</p></Card>
      ) : null}

      <RecoveryDialog
        open={Boolean(recovery)}
        mode={recovery?.mode}
        otherDevice={recovery?.otherDevice}
        startedAt={recovery?.record?.server?.startedAt || recovery?.summary?.startedAt}
        lastActivityAt={recovery?.record?.timing?.lastActivityAt || recovery?.summary?.lastActivityAt}
        offline={Boolean(recovery?.otherDevice && !online)}
        notice={lockBlocked && !confirmDiscard ? EXECUTION_OPEN_ELSEWHERE_MESSAGE : null}
        busy={busy || (recovery?.otherDevice && !online)}
        finishLocked={Boolean(recovery?.record) &&
          executionStatus(recovery.record) === "ACTIVE" &&
          executionCounts(recovery.record).performed === 0}
        onAction={act}
      />
      <Dialog
        open={Boolean(confirmDiscard)}
        onClose={() => setConfirmDiscard(null)}
        title={confirmDiscard?.empty ? "Nothing was logged — discard?" : "Discard workout?"}
        description={confirmDiscard?.empty
          ? "A workout needs at least one performed set or completed cardio item to be saved."
          : "This removes the open workout after it syncs. This can’t be undone."}
        footer={(
          <div className="lz-workout-dialog-actions">
            {lockBlocked ? (
              <p className="lz-workout-offline-reason" role="status">
                {EXECUTION_OPEN_ELSEWHERE_MESSAGE}
              </p>
            ) : null}
            {confirmDiscard?.empty ? (
              <Button
                variant="ghost"
                onClick={() => openLocation(navigate, executionEditorLocation(confirmDiscard.record))}
              >
                Keep Editing
              </Button>
            ) : (
              <Button variant="ghost" onClick={() => setConfirmDiscard(null)}>Keep Workout</Button>
            )}
            <Button variant="danger" disabled={busy} onClick={discardConfirmed}>Discard Workout</Button>
          </div>
        )}
      ><span /></Dialog>
    </section>
  );
}

export {
  FLAG_ENABLED,
  displayTemporalStatus,
  formatDateKey,
  isStale,
  occurrenceSelectionKey,
  occurrenceStartPath,
  selectLocalOpenExecutions,
  selectRecoverableServerExecutions,
  serverOwnedElsewhere,
};
